/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import * as d3 from 'd3';
import { useEffect, useMemo, useRef } from 'react';
import React from 'react';
import { PieArcDatum } from 'd3';
import { formatValue, Rectangle, intersectsAny } from 'utilities/utils';
import { NameValue, sumValuesReducer, Dimensions } from './../../interfaces';
import { GenericObject } from 'interfaces/GenericObject';
import { readableColor } from 'polished';
import { useHistory } from 'react-router';
import { fitLabelWidth } from 'widgets/chartUtils';

/**
 * A pie graph with innerRadius > 0
 */
const DonutGraph: React.FC<DonutGraphProps> = ({
	containerDimensions: dimensions,
	data,
	getColor,
	centerLabel = null,
	recordCountLabel: centerText = 'Total',
	innerRadiusPercent = 0.4,
	widgetConfiguration = {},
}) => {
	const radius = Math.min(dimensions.height, dimensions.width) / 2; 
	const { colorByValue = false, hideLabels, hyperLink } = widgetConfiguration;

	//Only show the labels if we have enough extra space on the sides, and the donut is big enough
	const diameter = radius * 2;
	const showLabels: boolean = (dimensions.width - diameter) > 100;
	const sliceLabelWidth = ((dimensions.width - diameter) / 2) - 12; //Find the available width on either side of the pie, then substract out a little more for padding

	const svgRef = useRef(null);
	const donutRef = useRef(null);
	//Used in mouse interactions. Otherwise the closure has the original value of colorByValue, which may have been updated
	const colorByValueRef = useRef(colorByValue);
	colorByValueRef.current = colorByValue;
	const getColorRef = useRef(getColor);
	getColorRef.current = getColor;

	//dataCache is needed for the slice animation.
	const dataCache = useRef<NameValue[]>([]);

	const mapColor = useMemo(() => {
		return data.map((d) => d.name);
	}, [data]);

	// use history to create links to pages within the chart
	const history = useHistory();

	const navigateTo = (link) => {
		history.push(link);
	};

	useEffect(() => {
		// Filter out 0 value data so that wedges aren't shown.
		const filteredData = data.filter(d => d.value !== 0);

		const totalRecords = filteredData.reduce(sumValuesReducer, 0);

		//The arc used to draw the pie slices
		const createSliceArc = createArc(radius * innerRadiusPercent, radius * (hideLabels ? 1.0 : 0.8));
		//The arc used to center the labels outside of the pie slice
		const createLabelArc = createArc(radius * 0.9); //label base should be just outside the outer radius
		//The arc used to center the % labels, just inside the pie
		const createPercentLabelArc = createArc(radius * 0.7); //percent label is just inside the outer radius
		//The arc that defines where the label line starts
		const createLineInsertionArc = createArc(radius * 0.75);

		//pieData is an array of coordinates used to draw a pie
		const pieData = createPie(filteredData);
		const prevPieData = createPie(dataCache.current);

		//interpolator used to animate the pie slices (grow from previous size to new size)
		const arcTween = (d: PieArcDatum<NameValue>, index: number) => {
			//default datum to use, if no previous pie slice exists.
			const initialDatum: PieArcDatum<NameValue> = {
				startAngle: 0,
				endAngle: 0,
				padAngle: 0,
				value: 0,
				index: index,
				data: { name: '', value: 0 },
			};
			const interpolator = d3.interpolate(prevPieData[index] ?? initialDatum, d);
			return (t: number) => createSliceArc(interpolator(t));
		};

		/***  draw the pie slices  ****/
		d3.select(donutRef.current)
			.selectAll('path')
			.data(pieData)
			.join(
				(enter) =>
					enter
						.append('path')
						.attr('d', createSliceArc)
						.attr('fill', (d: PieArcDatum<NameValue>) => getColorRef.current(d.data.colorValue))
						.attr('stroke', 'white')
						.attr('cy-test', 'donut-data-slice'),
				(update) =>
					update
						.attr('d', createSliceArc)
						.attr('fill', (d: PieArcDatum<NameValue>) => getColorRef.current(d.data.colorValue)),
				(exit) => exit.remove()
			)
			//on mouseover:
			//	 apply opacity to all non-hovered slices
			//	 put a thick black border on the hovered slice
			//   display additional details within the label of the hovered slice
			.on('mouseover', function () {
				// (d,i) no longer supported in v6, have to get the index this janky way.
				const index = d3.select(donutRef.current).selectAll('path').nodes().indexOf(this);

				d3.select(donutRef.current)
					.selectAll('path')
					.transition('hoverTransition')
					.duration(250)
					.attr('opacity', () => '0.8');
				d3.select(this)
					.transition('hoverTransition')
					.duration(250)
					.attr('opacity', () => '1')
					.attr('fill', (d: PieArcDatum<NameValue>) => {
						const color = d3.color(getColorRef.current(d.data.colorValue));
						if (color) {
							return color.darker().toString();
						} else {
							return null;
						}
					});

				d3.select(donutRef.current)
					.selectAll('.centerGroup .center-hover-text')
					.text(pieData.filter((d: PieArcDatum<NameValue>) => d.index === index)[0]?.data?.name);

				d3.select(donutRef.current)
					.selectAll('.percentageText')
					.text((d: PieArcDatum<NameValue>) => getDataPercentageText(totalRecords, d, d.index === index));

					
				d3.select(donutRef.current)
					.selectAll('.sliceLabelGroup')
					.classed('isHovered', (d: PieArcDatum<NameValue>) => (d.index === index))
					.style("opacity", (d: PieArcDatum<NameValue>) => d.index === index ? "1" : null)
					.filter(":not(.hideLabels)")
					.classed('deemphasized', (d: PieArcDatum<NameValue>) => (d.index !== index))

				const hoveredData: PieArcDatum<NameValue> = pieData.filter((d: PieArcDatum<NameValue>) => d.index === index)[0];
				const detailText = `${hoveredData.data.value}, ${(
					((hoveredData?.data.value || 0) / totalRecords) *
					100
				).toFixed(1)}%`;

				d3.select(donutRef.current).selectAll('.centerGroup .center-hover-text-details').text(detailText);
			})
			// on mouseout, put all the slices back to normal
			.on('mouseout', () => {
				const vizRef = d3.select(donutRef.current);
				vizRef
					.selectAll('path')
					.data(pieData)
					.transition('hoverTransition')
					.duration(250)
					.attr('opacity', '1')
					.attr('fill', (d: PieArcDatum<NameValue>) => getColorRef.current(d.data.colorValue));

				vizRef
					.selectAll('.percentageText')
					.text((d: PieArcDatum<NameValue>) => getDataPercentageText(totalRecords, d));

				vizRef
					.selectAll('.sliceLabelText')
					.attr('opacity', '1');

				vizRef
					.selectAll('.sliceLabelGroup')
					.classed('isHovered', false)
					.classed('deemphasized', false)
					.style("opacity", null);

				vizRef
					.selectAll('.centerGroup')
					.selectAll('.center-hover-text, .center-hover-text-details')
					.text('');
			})
			.transition()
			.duration(1000)
			.attrTween('d', arcTween);

		//center text and labels need to be removed on updates, then we'll fade them in
		d3.select(donutRef.current).selectAll('.sliceLabelText, .percentageText, .centerGroup, polyline').remove();

		//showLabels is based on widget container dimensions, hideLabels is based on switch in curated views and is set by user
		if (showLabels && !hideLabels) {

			d3.select(donutRef.current)
				.selectAll('sliceLabelGroup')
				.data(pieData)
				.join((enter) => {

					const group = enter.append('g').classed('sliceLabelGroup', true);

					
					//Add the Name property to the outside of the pie slice
					const labelPositionList = {};
					// add lines to connect labels to slices
					group
						.append('polyline')
						.attr('stroke', 'gray')
						.style('fill', 'none')
						.attr('stroke-width', 1)
						.attr('points', (d: PieArcDatum<NameValue>): any => {
							var posA = createLineInsertionArc.centroid(d); // line insertion in the slice
							var posB = createLabelArc.centroid(d);
							var posC = createLabelArc.centroid(d);
							var midangle = getPieSliceMidangle(d); // we need the angle to see if the X position will be at the extreme right or extreme left
							posC[0] = radius * 0.95 * (midangle < Math.PI ? 1 : -1); // multiply by 1 or -1 to put it on the right or on the left
							return [posA, posB, posC];
						});

					group
						.append('text')
						.classed('sliceLabelText', true)
						.text((d: PieArcDatum<NameValue>) => d.data.name )
						.style('text-anchor', (d: PieArcDatum<NameValue>) => {								
							return getPieSliceMidangle(d) < Math.PI ? 'start' : 'end';
						})
						.attr('font-size', '.9em')
						.style('alignment-baseline', 'middle')
						.attr('transform', (d: PieArcDatum<NameValue>) => {
							var pos = createLabelArc.centroid(d);
							pos[0] = radius * (getPieSliceMidangle(d) < Math.PI ? 1 : -1);
							return `translate(${pos})`;
						})
						.attr('cy-test', 'donut-data-slice-label')
						.style("cursor", () => hyperLink ? "pointer" : null)
						.on("click", (event,d) => {
							if(d?.data?.name && hyperLink) {
								if(hyperLink.includes("/curated_views/Organization")){
									//HACK for now. May want to include the orgs key? Or make hyperLink a func? (dont love that idea bc it comes
									// thru widgetConfig, which should be hydratable
									// https://stackoverflow.com/a/47864833
									const parsedKey = (d?.data?.name).match(/\((.*)\)/);
									const key = parsedKey ? parsedKey.pop() : d?.data?.name;
									if (key)
										window.open(`${hyperLink.replace("${}", key)}`, '_blank');
								}
								else{
									window.open(`${hyperLink.replace("${}", d?.data?.name)}`, '_blank');
								}
						}})
						.each(function(d) {
							var pos = createLabelArc.centroid(d);
							pos[0] = radius * (getPieSliceMidangle(d) < Math.PI ? 1 : -1);

							var self = d3.select(this);
							const bbox = self.node().getBBox();

							const rectangle: Rectangle = { height: bbox.height, width: bbox.width , x: pos[0], y: pos[1] };

							fitLabelWidth(12.6, "'Libre_Franklin',ArialMT,sans-serif",
									textLength => textLength > (sliceLabelWidth)).apply(this, arguments);

							
							if (!intersectsAny(rectangle, Object.values(labelPositionList))) {
								labelPositionList[d.data.name] = rectangle;
							}
						})
					
					// add the % on the inside the outer edge of the pie slice
					group
						.append('text')
						.classed('percentageText', true)
						.text((d: PieArcDatum<NameValue>) => getDataPercentageText(totalRecords, d))
						.attr('font-size', '10px')
						.attr('pointer-events', 'none') // disable pointer events so that the % doesn't steal the hover event from the slice
						.style('text-anchor', 'middle')
						.style('fill', (d: PieArcDatum<NameValue>) => {
							const color = getColorRef.current(d.data.colorValue);
							return color ? readableColor(color) : 'black';
						})
						.style('dominant-baseline', 'middle')
						.attr('transform', (d: PieArcDatum<NameValue>) => 'translate(' + createPercentLabelArc.centroid(d) + ')')
						.attr('cy-test', 'donut-data-slice-text');

						
					group.classed('hidden', (d) => !!centerLabel || !labelPositionList[d.data.name]);

					return group;
				});
		}
		
		// don't use em or rem for font-size in svg
		const centerTextFontSize = Math.round(Math.min(24, Math.max(radius / 10, 14))); //px
		const detailsTextFontSize = 12; //px
		const textLineSpacing = 8; //px

		d3.select(donutRef.current).selectAll('.centerGroup').remove();
		let centerGroup = d3
			.select(donutRef.current)
			.append('g')
			.classed('centerGroup', true)
			.style('font-size', centerLabel ? `${centerTextFontSize}px` : `${centerTextFontSize}px`)
			.style('text-anchor', 'middle');
		if (!centerLabel) {
			centerGroup
				.append('text')
				.text(formatValue(totalRecords))
				.attr('y', `-${(centerTextFontSize)}px`)
				.style('font-weight', 'bold')
				.attr('cy-test', 'donut-data-center-total-records');
			centerGroup
				.append('text')
				.text(centerText)
				.attr('y', `${(centerTextFontSize*0)}px`);
			centerGroup
				.append('text')
				.classed('center-hover-text', true)
				.text('')
				.style('font-size', `${detailsTextFontSize}px`)
				.attr('y', `${(detailsTextFontSize + textLineSpacing)}px`);
			centerGroup
				.append('text')
				.classed('center-hover-text-details', true)
				.text('')
				.style('font-size', `${detailsTextFontSize}px`)
				.attr('y', `${(detailsTextFontSize * 2 + textLineSpacing)}px`);
		} else {
			centerGroup
				.append('text')
				.text(centerLabel)
				// Center the labels for multichart
				.style('dominant-baseline', 'central');
		}

		//animate the labels
		d3.select(donutRef.current)
			.selectAll('.sliceLabelText, .percentageText, .centerGroup, polyline')
			.transition()
			.duration(1500)
			.ease(d3.easeExp)
			.styleTween('opacity', () => d3.interpolate('0', '1'));

		//dataCache is needed for the slice animation. Set the cache to the current data so we have it for the next render
		dataCache.current = data;
	}, [data, radius, centerText, dimensions, innerRadiusPercent, mapColor, centerLabel, showLabels, sliceLabelWidth]);

	useEffect(() => {
		//draw the pie slices
		d3.select(donutRef.current)
			.selectAll('path')
			.attr('fill', (d: PieArcDatum<NameValue>) => getColorRef.current(d.data.colorValue));
	}, [colorByValue]);

	useEffect(() => {
		const refSelection = d3.select(donutRef.current);
		refSelection
			.selectAll('.sliceLabelGroup, .centerGroup')
			.classed('hideLabels', hideLabels);
		refSelection //the opacity it set in style for this comp (to tween it i guess) so need to specifically set it here
			.selectAll('.centerGroup')
			.style("opacity", hideLabels ? "0" : "1");

		const createSliceArc = createArc(radius * innerRadiusPercent, radius * (hideLabels ? 1.0 : 0.8));
		refSelection
			.selectAll('path')
			.transition()
			.attr('d', createSliceArc)
	}, [hideLabels]);

	return (
		<div css={donutCss} >
			<svg cy-test="donut-data" width={dimensions.width} height={dimensions.height} ref={svgRef}>
				<g ref={donutRef} transform={`translate(${dimensions.width / 2} ${dimensions.height / 2})`} />
			</svg>
		</div>
	);
};

export { DonutGraph };

const donutCss = css`
	.hideLabels {
		opacity: 0;
	}
	
	.sliceLabelGroup:not(.isHovered){
		&.hidden{
			display: none;
		}
		&.deemphasized{
			opacity: .3;
			font-size: .9em;
		}
	}

	.sliceLabelGroup.isHovered{		
		font-weight: 800;
		font-size: 1.1em;
	}

	.centerGroup /*, text, tspan */ {
		/* outline text for overlap with bars (this may be a performance hit?) */
		text-shadow: 0 0 2px white, 0 0 2px white, 0 0 2px white;
	}
`;


/**
 * A function to generate coordiates that can be used to generate a pie
 */
const createPie = d3
	.pie<NameValue>()
	.value((nameValuePair) => nameValuePair.value)
	.sort(null);

/**
 * base function to create the arcs for the pice slices and labels
 * @param innerRadius
 * @param outerRadius
 */
const createArc = (innerRadius: number, outerRadius?: number) => {
	return d3
		.arc<PieArcDatum<NameValue>>()
		.innerRadius(innerRadius)
		.outerRadius(outerRadius || innerRadius);
};

/**
 * Function to get a midangle of a slice
 * @param d
 */
const getPieSliceMidangle = (d: PieArcDatum<NameValue>) => d.startAngle + (d.endAngle - d.startAngle) / 2;

/**
 * get the percentage with that "%" character appended. Optional parameter to specify rounding
 * @param totalRecords
 * @param d
 * @param fractionDigits
 */
const getDataPercentageText = (totalRecords: number, d: PieArcDatum<NameValue>, isHovered?: boolean): string => {
	const percentageText = `${(((d.data.value || 0) / totalRecords) * 100).toFixed(isHovered ? 1 : 0)}%`;

	if (isHovered) {
		return `${d.data.value}, ${percentageText}`;
	} else {
		return percentageText;
	}
};

interface DonutGraphProps {
	/**
	 * The dimensions of the element that contains the donut
	 */
	containerDimensions: Dimensions;

	/**
	 * Array of NameValue objects representing the data to display in the donut
	 */
	data: NameValue[];

	/**
	 * function that when passed a value will return the color to use
	 */
	getColor: (value: number | string) => string;

	/**
	 * Function to call when a pie slice is clicked (selected)
	 */
	onSelect: SelectFunc;

	/**
	 * The widget config object
	 */
	widgetConfiguration: GenericObject<any>;

	/**
	 * If this is set show this directly instead of the record count
	 * Default: null
	 */
	centerLabel: string;

	/**
	 * The text to append the to the sum of total records, display in the center of the donut
	 * Default: "Total"
	 */
	recordCountLabel: string;

	/**
	 * The size of the inner radius, as a percentage of the smallest dimension
	 * E.g. 20 will render a donut with an inner radius is 20% of min(height, width)
	 */
	innerRadiusPercent: number;
}

interface SelectFunc {
	(args);
}

